import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { getCategories } from '../services/categoryService';
import { getSubcategories } from '../services/subcategoryService';
import { getTags } from '../services/tagService';
import { getProducts } from '../services/productService';

const DataContext = createContext();

// ============================================
// FALLBACK DATA - Show immediately while API loads
// ============================================
const FALLBACK_CATEGORIES = [
    { _id: 'cat-1', name: 'Skincare', slug: 'skincare', description: 'Cleansers, moisturizers, serums, and treatments.', isActive: true },
    { _id: 'cat-2', name: 'Hair', slug: 'hair', description: 'Shampoos, conditioners, and treatments.', isActive: true },
    { _id: 'cat-3', name: 'Makeup', slug: 'makeup', description: 'Foundation, concealer, and color cosmetics.', isActive: true },
    { _id: 'cat-4', name: 'Body', slug: 'body', description: 'Body lotions, scrubs, and treatments.', isActive: true },
    { _id: 'cat-5', name: 'Wellness', slug: 'wellness', description: 'Supplements and self-care products.', isActive: true },
    { _id: 'cat-6', name: 'Health', slug: 'health', description: 'Health essentials and supplements.', isActive: true }
];

export const DataProvider = ({ children }) => {
    // Initialize with fallback data for INSTANT render
    const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
    const [subcategories, setSubcategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [products, setProducts] = useState([]);
    
    const [loading, setLoading] = useState(false);
    const [dataReady, setDataReady] = useState(false);
    const [error, setError] = useState(null);

    // ============================================
    // FETCH ALL DATA - Optimized parallel loading
    // Products are fetched with NO limit to get all items
    // ============================================
    const fetchAllData = async () => {
        try {
            setError(null);
            const startTime = performance.now();
            
            // Fetch all data in PARALLEL
            const [
                categoriesResponse,
                subcategoriesResponse,
                tagsResponse,
                productsResponse
            ] = await Promise.all([
                getCategories(),
                getSubcategories(),
                getTags(),
                // NO LIMIT - fetch all products
                // Backend handles this efficiently with .lean() and indexes
                getProducts({ sort: '-createdAt' })
            ]);

            const endTime = performance.now();
            console.log(`⚡ DataContext - All data fetched in ${(endTime - startTime).toFixed(0)}ms`);

            // Process categories
            const categoryData = categoriesResponse.data || [];
            const activeCategories = categoryData.filter(category => category.isActive !== false);
            setCategories(activeCategories);

            // Process subcategories
            const subcategoryData = subcategoriesResponse.data || [];
            const activeSubcategories = subcategoryData.filter(subcategory => subcategory.isActive !== false);
            setSubcategories(activeSubcategories);

            // Process tags
            const tagData = tagsResponse.data || [];
            setTags(tagData);

            // Process products
            const productData = productsResponse.data || [];
            console.log(`📦 DataContext - Loaded ${productData.length} products (total: ${productsResponse.total || productData.length})`);
            setProducts(productData);

        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load data. Please try again later.');
        } finally {
            setDataReady(true);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    // Helper function to get subcategories for a specific category
    const getSubcategoriesForCategory = (categoryId) => {
        return subcategories.filter(sub => sub.category === categoryId ||
            (sub.category && sub.category._id === categoryId));
    };

    // Helper function to get tags for a specific subcategory
    const getTagsForSubcategory = (subcategoryId) => {
        return tags.filter(tag => tag.subcategory === subcategoryId ||
            (tag.subcategory && tag.subcategory._id === subcategoryId));
    };

    // Helper function to get products for a specific category
    const getProductsForCategory = (categoryId) => {
        return products.filter(product => product.category === categoryId ||
            (product.category && product.category._id === categoryId));
    };

    // Helper function to get products for a specific subcategory
    const getProductsForSubcategory = (subcategoryId) => {
        return products.filter(product => product.subcategory === subcategoryId ||
            (product.subcategory && product.subcategory._id === subcategoryId));
    };

    // Helper function to get featured products
    const getFeaturedProducts = (categoryId = null) => {
        let featuredProducts = products.filter(product => product.isFeatured);

        if (categoryId) {
            featuredProducts = featuredProducts.filter(product =>
                product.category === categoryId ||
                (product.category && product.category._id === categoryId)
            );
        }

        return featuredProducts;
    };

    // Helper function to get products by tag
    const getProductsByTag = (tagId) => {
        return products.filter(product =>
            product.tags && (
                product.tags.includes(tagId) ||
                product.tags.some(tag =>
                    (typeof tag === 'object' && tag._id === tagId) || tag === tagId
                )
            )
        );
    };

    // Helper function to find category by slug
    const getCategoryBySlug = (slug) => {
        return categories.find(category => category.slug === slug);
    };

    // Helper function to find subcategory by slug
    const getSubcategoryBySlug = (slug) => {
        return subcategories.find(subcategory => subcategory.slug === slug);
    };

    const refreshData = async () => {
        await fetchAllData();
    };

    const value = {
        categories,
        subcategories,
        tags,
        products,
        loading,
        dataReady,
        error,
        refreshData,
        getSubcategoriesForCategory,
        getTagsForSubcategory,
        getProductsForCategory,
        getProductsForSubcategory,
        getFeaturedProducts,
        getProductsByTag,
        getCategoryBySlug,
        getSubcategoryBySlug
    };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
};

// Custom hook to use the data context
export const useData = () => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};
